'use client';

import { Button } from '@/components/ui/buttons/Button';
import { useTranslation } from '@/features/i18n';
import { Input } from '@/ui/form/Input';
import { Label } from '@/ui/form/Label';
import { logger } from '@/utils/loggerUtils';
import { zodResolver } from '@hookform/resolvers/zod';
import { authenticateUserInRestAPI } from '@vimond/services/rest-api';
import { confirmSignIn } from 'aws-amplify/auth';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

export const SetNewPasswordForm = () => {
  const { t } = useTranslation();

  const [errorMessage, setErrorMessage] = useState<string>('');

  const passwordSchema = z
    .string()
    .min(8, {
      message: t(
        'account.password.password-must-be-at-least-8-characters-long',
      ),
    })
    .regex(/[a-z]/, {
      message: t('account.password.must-contain-lowercase'),
    })
    .regex(/[A-Z]/, {
      message: t('account.password.must-contain-uppercase'),
    })
    .regex(/\d/, {
      message: t('account.password.must-contain-number'),
    })
    .regex(/[@$!%*?&]/, {
      message: t('account.password.must-contain-special-character'),
    })
    .refine((val) => val === val.trim(), {
      message: t(
        'account.password.password-must-not-contain-leading-or-trailing-spaces',
      ),
    });

  const SetNewPasswordFormSchema = z
    .object({
      newPassword: passwordSchema,
      newPasswordConfirm: passwordSchema,
    })
    .refine(
      ({ newPassword, newPasswordConfirm }) =>
        newPassword === newPasswordConfirm,
      {
        message: t('account.password.passwords-must-match'),
        path: ['newPasswordConfirm'],
      },
    );

  type SetNewPasswordFormSchemaType = z.infer<typeof SetNewPasswordFormSchema>;
  const {
    formState: { errors, isDirty, isLoading, isSubmitting },
    handleSubmit,
    register,
  } = useForm<SetNewPasswordFormSchemaType>({
    resolver: zodResolver(SetNewPasswordFormSchema),
  });

  const onNewPasswordFormSubmit: SubmitHandler<
    SetNewPasswordFormSchemaType
  > = async ({ newPassword }) => {
    await confirmSignIn({ challengeResponse: newPassword })
      .then(async (res) => {
        logger.log(res);
        if (res.isSignedIn) {
          await authenticateUserInRestAPI();
          setErrorMessage('');
          // redirect to who is watching page using window.location.href so that the cookies are available for both server and client
          window.location.href = '/profiles/whoiswatching';
        }
      })
      .catch((err: Error) => {
        logger.error(err);
        setErrorMessage(t('auth.password-update-failed-please-try-again'));
      });
  };

  return (
    <div className="space-y-4 p-6 sm:p-8 md:space-y-6">
      <form
        className="space-y-4 md:space-y-6"
        onSubmit={handleSubmit(onNewPasswordFormSubmit)}
      >
        <h1 className="text-center text-xl font-bold leading-tight tracking-tight md:text-2xl">
          {t('account.password.headline')}
        </h1>
        <div className="flex flex-wrap">
          <div className="w-full px-4">
            <div className="relative mb-4 w-full space-y-1">
              <div className="flex items-center justify-between">
                <Label htmlFor="newPassword">
                  {t('account.password.new-password')}
                </Label>
              </div>
              <Input
                id="newPassword"
                type="password"
                placeholder="********"
                {...register('newPassword')}
                aria-describedby="newPassword-error"
              />
              <p
                id="newPassword-error"
                className="text-red-500"
                aria-live="polite"
                aria-atomic="true"
              >
                {errors.newPassword?.message}
              </p>
            </div>
          </div>

          <div className="w-full px-4">
            <div className="relative w-full space-y-1">
              <div className="flex items-center justify-between">
                <Label htmlFor="newPasswordConfirm">
                  {t('account.password.confirm-new-password')}
                </Label>
              </div>
              <Input
                id="newPasswordConfirm"
                type="password"
                placeholder="********"
                {...register('newPasswordConfirm')}
                aria-describedby="newPasswordConfirm-error"
              />
              <p
                id="newPasswordConfirm-error"
                className="text-red-500"
                aria-live="polite"
                aria-atomic="true"
              >
                {errors.newPasswordConfirm?.message}
              </p>
            </div>
          </div>
        </div>
        <div>
          <div className="border-background-secondary flex-col space-y-2 pl-4 pr-4 mb-3">
            <Button
              variant="primary"
              size="sm"
              className="w-full"
              type="submit"
              disabled={isLoading || isSubmitting || !isDirty}
            >
              {t('save-new-password')}
            </Button>
            {errorMessage && <p className="text-red-500">{errorMessage}</p>}
          </div>
        </div>
      </form>
    </div>
  );
};
